import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TranslateService } from '@ngx-translate/core';
import {
  getReportWidgetSlotDefinitions,
  getReportWidgetTemplateDefinition,
  isReportWidgetChartRenderer,
  parseReportWidgetTemplateConfig,
  ReportWidgetRenderData,
  ReportWidgetSlotDefinition,
  ReportWidgetTemplateConfig
} from './report-widget-template.model';
import { ReportWidgetTemplateRenderApi } from './report-widget-template-render-api.model';

@Component({
  selector: 'app-report-widget-renderer',
  templateUrl: './report-widget-renderer.component.html',
  styleUrls: ['./report-widget-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ReportWidgetRendererComponent implements ReportWidgetTemplateRenderApi {
  @Input() data: ReportWidgetRenderData | null = null;
  @Input() templateConfigJson?: string | null;
  @Input() templateKey?: string | null;
  @Input() rendererKey?: string | null;
  @Input() highlightFirst = true;
  @Input() loading = false;
  @Input() variant: 'preview' | 'dashboard' | string = 'dashboard';
  @Input() title = '';
  @Input() rowLimit?: number | null;

  constructor(private translate: TranslateService) {}

  get config(): ReportWidgetTemplateConfig {
    return parseReportWidgetTemplateConfig(this.templateConfigJson || this.data?.templateConfigJson);
  }

  get renderer(): string {
    const config = this.config;
    const metadataRenderer = this.data?.metadata?.['rendererKey'];
    const resolved = metadataRenderer || config.rendererKey || this.rendererKey || this.data?.templateKey || this.templateKey || 'top-list';
    return String(resolved || 'top-list').trim() || 'top-list';
  }

  get resolvedTemplateKey(): string {
    const config = this.config;
    return String(
      this.data?.metadata?.['templateKey'] ||
      config.templateKey ||
      config.presetKey ||
      this.data?.templateKey ||
      this.templateKey ||
      this.renderer
    ).trim();
  }

  get visualVariant(): string {
    const config = this.config;
    return String(
      this.data?.metadata?.['visualVariant'] ||
      config.visualVariant ||
      this.resolvedTemplateKey ||
      this.renderer
    ).trim();
  }

  get slotDefinitions(): ReportWidgetSlotDefinition[] {
    return getReportWidgetSlotDefinitions(this.resolvedTemplateKey, this.renderer, this.config);
  }

  get supportedSlotRoles(): Set<string> {
    return new Set(this.slotDefinitions.flatMap((slot) => [slot.key, slot.role]));
  }

  get rows(): Record<string, any>[] {
    const rows = Array.isArray(this.data?.rows) ? this.data.rows : [];
    const limit = this.resolveRowLimit();
    return limit > 0 ? rows.slice(0, limit) : rows;
  }

  get summaryEntries(): { key: string; value: any }[] {
    const summary = this.data?.summary || {};
    return Object.keys(summary).map((key) => {
      const calculation = this.findCalculation(key, 'summary');
      return {
        key,
        value: this.formatValue(summary[key], calculation?.format || '')
      };
    });
  }

  get errors(): string[] {
    return Array.isArray(this.data?.errors) ? this.data.errors : [];
  }

  get hasData(): boolean {
    return !!this.data && (
      this.rows.length > 0 ||
      this.summaryEntries.length > 0 ||
      Object.keys(this.data?.slots || {}).length > 0 ||
      Object.keys(this.data?.metadata || {}).length > 0
    );
  }

  get isChart(): boolean {
    return isReportWidgetChartRenderer(this.renderer) && !!this.chartMetadata;
  }

  get isPieChart(): boolean {
    return this.chartMetadata?.chartKind === 'pie';
  }

  get isKpi(): boolean {
    const definition = getReportWidgetTemplateDefinition(this.resolvedTemplateKey, this.renderer, this.config);
    return this.renderer === 'kpi-cards' || definition.capabilities.summary === true;
  }

  get isCompactTable(): boolean {
    return this.renderer === 'compact-table';
  }

  get isTopOne(): boolean {
    return this.renderer === 'top-one';
  }

  get isTopList(): boolean {
    return this.renderer === 'top-list';
  }

  get isAlertList(): boolean {
    return this.renderer === 'alert-threshold-list' || this.matchesVisualVariant(['alert', 'alert-compact', 'threshold-list']);
  }

  get chartMetadata(): any {
    return this.data?.metadata?.['chart'] || null;
  }

  get displayHeaders(): string[] {
    const headers = this.data?.headers || [];
    const config = this.config;
    const configuredColumns = Array.isArray(config.displayColumns) ? config.displayColumns : [];
    const explicitColumns = Array.isArray(config.displayColumns);
    const requested = this.uniqueColumns(configuredColumns);

    if (!requested.length) {
      return explicitColumns ? [] : headers;
    }

    const rows = this.rows;
    const resolved = requested
      .map((column) => this.resolveHeader(column, headers, rows))
      .filter((column): column is string => !!column);

    return this.uniqueColumns(resolved);
  }

  get hasRenderableSlots(): boolean {
    return this.slotDefinitions
      .filter((slot) => slot.role !== 'color')
      .some((slot) => !!this.getSlotBinding(slot.key));
  }

  get slotLabelsVisible(): boolean {
    const formatting = this.config.formatting || {};
    return this.config.hideLabels !== true && formatting['hideLabels'] !== true;
  }

  get rankedByLabel(): string {
    return this.getSlotColumnLabel('primaryValue') || this.getSlotColumnLabel('subtitle') || '';
  }

  get rendererTitle(): string {
    return this.normalizeHeaderText(this.config.header?.title)
      || this.title
      || this.data?.reportName
      || this.resolvedTemplateKey
      || 'Widget';
  }

  get rendererSubtitle(): string {
    return this.normalizeHeaderText(this.config.header?.subtitle);
  }

  get chartDataSource(): any[] {
    return Array.isArray(this.chartMetadata?.dataSource) ? this.chartMetadata.dataSource : [];
  }

  get chartSeries(): any[] {
    const series = Array.isArray(this.chartMetadata?.series) ? this.chartMetadata.series : [];
    return series.map((item) => {
      if (item?.comparison !== true) {
        return item;
      }

      const labelKey = this.chartMetadata?.comparison?.labelKey || '';
      const fallback = 'Previous period';
      const baseName = item.baseName || item.name || '';
      return {
        ...item,
        name: baseName ? `${baseName} (${labelKey || fallback})` : (labelKey || fallback)
      };
    });
  }

  get chartType(): any {
    return this.chartMetadata?.type || 'bar';
  }

  get chartPalette(): any {
    return this.chartMetadata?.palette || 'Soft Pastel';
  }

  get chartArgumentField(): string {
    return this.chartMetadata?.argumentField || '';
  }

  get chartValueField(): string {
    return this.chartMetadata?.valueField || '';
  }

  get chartRotated(): boolean {
    return this.chartMetadata?.rotated === true;
  }

  get chartLegendVisible(): boolean {
    return this.chartMetadata?.legendVisible !== false;
  }

  get chartLabelsVisible(): boolean {
    return this.chartMetadata?.labelsVisible !== false;
  }

  get templateApi(): ReportWidgetTemplateRenderApi {
    return this;
  }

  accentStyle(row: Record<string, any> | null): Record<string, string> {
    const style = this.normalizeStyleObject(row?.['__style']?.accent || {});
    if (!style['background-color'] && style.color) {
      style['background-color'] = style.color;
    }

    delete style.color;
    return style;
  }

  topListRowStyle(row: Record<string, any> | null): Record<string, string> {
    return this.rowStyle(row);
  }

  rowStyle(row: Record<string, any> | null): Record<string, string> {
    return this.normalizeStyleObject(row?.['__style']?.row || {});
  }

  cellStyle(row: Record<string, any> | null, header: string): Record<string, string> {
    return this.normalizeStyleObject(this.rawCellStyle(row, header));
  }

  cellLeftDecorations(row: Record<string, any> | null, header: string): any[] {
    return this.rawDecorations(this.rawCellStyle(row, header), 'left');
  }

  cellRightDecorations(row: Record<string, any> | null, header: string): any[] {
    return this.rawDecorations(this.rawCellStyle(row, header), 'right');
  }

  cellBadgeLabel(row: Record<string, any> | null, header: string): string {
    const style = this.rawCellStyle(row, header);
    return String(style?.label || style?.badgeLabel || '').trim();
  }

  cellBadgeStyle(row: Record<string, any> | null, header: string): Record<string, string> {
    return this.normalizeStyleObject(this.rawCellStyle(row, header));
  }

  cellIconName(row: Record<string, any> | null, header: string): string {
    const style = this.rawCellStyle(row, header);
    return this.normalizeIconName(style?.name || style?.icon);
  }

  cellIconProp(row: Record<string, any> | null, header: string): IconProp {
    return ['fal', this.cellIconName(row, header) || 'circle-info'] as IconProp;
  }

  cellIconStyle(row: Record<string, any> | null, header: string): Record<string, string> {
    return this.normalizeStyleObject(this.rawCellStyle(row, header));
  }

  slotValue(row: Record<string, any> | null, key: string): string {
    const column = this.getSlotBinding(key);
    if (!column) {
      return '';
    }

    if (column.startsWith('summary.')) {
      const key = column.slice('summary.'.length);
      return this.formatValue(this.data?.summary?.[key], this.findCalculation(key, 'summary')?.format || '');
    }

    return this.displayCell(row, column);
  }

  hasSlotBinding(key: string): boolean {
    return !!this.getSlotBinding(key);
  }

  showSlotLabels(): boolean {
    return this.slotLabelsVisible;
  }

  slotColumnLabel(key: string): string {
    return this.getSlotColumnLabel(key);
  }

  slotCellStyle(row: Record<string, any> | null, key: string): Record<string, string> {
    return this.normalizeStyleObject(this.rawSlotCellStyle(row, key));
  }

  slotLeftDecorations(row: Record<string, any> | null, key: string): any[] {
    return this.rawDecorations(this.rawSlotCellStyle(row, key), 'left');
  }

  slotRightDecorations(row: Record<string, any> | null, key: string): any[] {
    return this.rawDecorations(this.rawSlotCellStyle(row, key), 'right');
  }

  slotIconName(row: Record<string, any> | null, key: string): string {
    const column = this.getSlotBinding(key);
    return column ? this.cellIconName(row, column) : '';
  }

  slotIconProp(row: Record<string, any> | null, key: string): IconProp {
    const column = this.getSlotBinding(key);
    return column ? this.cellIconProp(row, column) : ['fal', 'circle-info'] as IconProp;
  }

  slotIconStyle(row: Record<string, any> | null, key: string): Record<string, string> {
    const column = this.getSlotBinding(key);
    return column ? this.cellIconStyle(row, column) : {};
  }

  slotBadgeLabel(row: Record<string, any> | null, key: string): string {
    const column = this.getSlotBinding(key);
    return column ? this.cellBadgeLabel(row, column) : '';
  }

  slotBadgeStyle(row: Record<string, any> | null, key: string): Record<string, string> {
    const column = this.getSlotBinding(key);
    return column ? this.cellBadgeStyle(row, column) : {};
  }

  badgeLabel(row: Record<string, any> | null): string {
    const styleLabel = row?.['__style']?.badge?.label;
    if (styleLabel) {
      return String(styleLabel);
    }

    return this.supportedSlotRoles.has('badge') ? this.slotValue(row, 'badge') : '';
  }

  badgeStyle(row: Record<string, any> | null): Record<string, string> {
    return this.normalizeStyleObject(row?.['__style']?.badge || {});
  }

  iconName(row: Record<string, any> | null): string {
    const styleIcon = row?.['__style']?.icon;
    return this.normalizeIconName(styleIcon?.name || styleIcon?.icon || styleIcon);
  }

  iconProp(row: Record<string, any> | null): IconProp {
    return ['fal', this.iconName(row) || 'circle-info'] as IconProp;
  }

  iconStyle(row: Record<string, any> | null): Record<string, string> {
    return this.normalizeStyleObject(row?.['__style']?.icon || {});
  }

  decorationType(decoration: any): string {
    return String(decoration?.type || '').trim();
  }

  decorationStyle(decoration: any): Record<string, string> {
    const style = this.normalizeStyleObject(decoration || {});
    const type = this.decorationType(decoration);
    if ((type === 'accentDot' || type === 'accentBar') && !style['background-color'] && style.color) {
      style['background-color'] = style.color;
      delete style.color;
    }

    return style;
  }

  decorationIconProp(decoration: any): IconProp {
    return ['fal', this.normalizeIconName(decoration?.name || decoration?.icon) || 'circle-info'] as IconProp;
  }

  decorationBadgeLabel(decoration: any): string {
    return String(decoration?.label || decoration?.badgeLabel || '').trim();
  }

  displayCell(row: Record<string, any> | null, header: string): string {
    const rawValue = this.rawCell(row, header);
    const calculation = this.findCalculation(header, 'row');
    return this.formatValue(rawValue, calculation?.format || '');
  }

  rawCell(row: Record<string, any> | null, header: string): any {
    const rowKey = this.resolveRowKey(row, header);
    return rowKey && row ? row[rowKey] ?? '-' : '-';
  }

  trackHeader(index: number, item: string): string {
    return item || String(index);
  }

  trackSlot(_: number, item: ReportWidgetSlotDefinition): string {
    return item.key;
  }

  trackDecoration = (index: number, item: any): string => {
    return `${index}:${this.decorationType(item)}:${item?.label || item?.name || item?.icon || ''}`;
  };

  private resolveRowLimit(): number {
    const input = this.rowLimit ?? this.config.rowLimit ?? this.data?.displayRowCount ?? 0;
    const value = Number(input || 0);
    return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
  }

  private normalizeHeaderText(value: unknown): string {
    return String(value || '').trim();
  }

  private getSlotBinding(keyOrRole: string): string {
    const slot = this.slotDefinitions.find((item) => item.key === keyOrRole)
      || this.slotDefinitions.find((item) => item.role === keyOrRole);
    if (!slot) {
      return '';
    }

    const bindings = this.config.slotBindings && typeof this.config.slotBindings === 'object'
      ? this.config.slotBindings
      : {};
    const binding = String(bindings?.[slot.key] || bindings?.[slot.role] || '').trim();
    return binding && this.isSlotBindingAllowed(binding) ? binding : '';
  }

  private isSlotBindingAllowed(binding: string): boolean {
    if (!binding || binding.startsWith('summary.')) {
      return true;
    }

    if (!Array.isArray(this.config.displayColumns)) {
      return true;
    }

    const displayColumns = this.uniqueColumns(this.config.displayColumns);
    if (!displayColumns.length) {
      return false;
    }

    const bindingKey = this.lookupKey(binding);
    return displayColumns.some((column) => this.lookupKey(column) === bindingKey);
  }

  private getSlotColumnLabel(keyOrRole: string): string {
    const column = this.getSlotBinding(keyOrRole);
    if (!column) {
      return '';
    }

    if (column.startsWith('summary.')) {
      const summaryKey = column.slice('summary.'.length);
      return this.resolveColumnLabel(summaryKey, summaryKey);
    }

    const resolvedHeader = this.resolveHeader(column, this.data?.headers || [], this.rows);
    return this.resolveColumnLabel(column, resolvedHeader || column);
  }

  private resolveColumnLabel(column: string, fallback: string): string {
    const translated = this.resolveColumnTranslation(column) || this.resolveColumnTranslation(fallback);
    if (translated) {
      return translated;
    }

    const labels = this.data?.metadata?.['columnLabels'];
    if (labels && typeof labels === 'object') {
      const labelMap = labels as Record<string, any>;
      const direct = String(labelMap[column] || labelMap[fallback] || '').trim();
      if (direct) {
        return direct;
      }

      const columnLookup = this.lookupKey(column);
      const fallbackLookup = this.lookupKey(fallback);
      const matchedKey = Object.keys(labelMap).find((key) =>
        this.lookupKey(key) === columnLookup || this.lookupKey(key) === fallbackLookup
      );
      if (matchedKey) {
        const matchedLabel = String(labelMap[matchedKey] || '').trim();
        if (matchedLabel) {
          return matchedLabel;
        }
      }
    }

    return fallback;
  }

  private resolveColumnTranslation(column: string | null | undefined): string {
    const key = String(column || '').trim();
    if (!key) {
      return '';
    }

    const direct = this.translateColumnKey(key);
    if (direct) {
      return direct;
    }

    const pascalKey = key
      .replace(/(^|[_\-\s]+)([a-zA-Z0-9])/g, (_match, _separator, character) => String(character).toUpperCase())
      .replace(/[^a-zA-Z0-9]/g, '');
    const keyVariants = this.getColumnTranslationKeyVariants(pascalKey);
    const candidates = [
      ...this.getReportHeaderTranslationPrefixes().flatMap((prefix) => keyVariants.map((variant) => `${prefix}.${variant}`)),
      ...keyVariants.map((variant) => `SystemAdmin.Components.TableBase.Header.${variant}`)
    ];

    for (const candidate of candidates) {
      const translated = this.translateColumnKey(candidate);
      if (translated) {
        return translated;
      }
    }

    return '';
  }

  private translateColumnKey(key: string): string {
    const translated = this.translate.instant(key);
    return typeof translated === 'string' && translated.trim() && translated !== key
      ? translated
      : '';
  }

  private getColumnTranslationKeyVariants(pascalKey: string): string[] {
    const variants = [pascalKey];
    const technicalSuffixes = [
      'Seconds',
      'Milliseconds',
      'Minutes',
      'Hours',
      'At',
      'Utc',
      'Local'
    ];

    for (const suffix of technicalSuffixes) {
      if (pascalKey.endsWith(suffix) && pascalKey.length > suffix.length) {
        variants.push(pascalKey.slice(0, -suffix.length));
      }
    }

    if (pascalKey.endsWith('Id')) {
      variants.push(`${pascalKey.slice(0, -2)}Name`, pascalKey.slice(0, -2));
    }

    if (pascalKey.endsWith('Name')) {
      variants.push(pascalKey.slice(0, -4));
    }

    if (pascalKey.endsWith('Description')) {
      variants.push(pascalKey.slice(0, -11), 'Description');
    }

    return variants.filter((variant, index, items) => !!variant && items.indexOf(variant) === index);
  }

  private getReportHeaderTranslationPrefixes(): string[] {
    const groupKey = String(this.data?.reportGroupKey || '').trim();
    const map: Record<string, string[]> = {
      Sensor: ['LiveTagProperty.List.Table.Header', 'TagPropertyReport.List.Table.Header'],
      SensorHistory: ['TagPropertyReport.List.Table.Header', 'LiveTagProperty.List.Table.Header'],
      SensorAlertHistory: ['TagPropertyAlertReport.List.Table.Header'],
      Location: ['LiveTagLocation.List.Table.Header', 'TrackingReport.List.Table.Header'],
      LocationHistory: ['TrackingReport.List.Table.Header', 'LiveTagLocation.List.Table.Header'],
      ReaderPropertyHistory: ['ReaderPropertyReport.List.Table.Header'],
      ReaderHealthHistory: ['ReaderPropertyReport.List.Table.Header'],
      AccessRuleDwell: ['AccessRuleDwellReport.List.Table.Header'],
      PresenceSummary: ['PresenceSummaryReport.List.Table.Header'],
      ZoneEngagement: ['ZoneEngagementReport.List.Table.Header']
    };

    return map[groupKey] || (groupKey ? [`${groupKey}.List.Table.Header`] : []);
  }

  private findCalculation(key: string, scope: 'row' | 'summary'): any | null {
    const calculations = Array.isArray(this.config.calculations) ? this.config.calculations : [];
    const candidate = this.lookupKey(key);
    return calculations.find((calculation) => {
      if (calculation?.scope !== scope) {
        return false;
      }

      return [calculation.id, calculation.label]
        .map((item) => this.lookupKey(item))
        .includes(candidate);
    }) || null;
  }

  private formatValue(value: any, format: string): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (!format) {
      return String(value);
    }

    const numericValue = this.toNumericValue(value);
    if (numericValue === null) {
      return String(value);
    }

    const suffix = Array.from(format.matchAll(/"([^"]*)"/g))
      .map((match) => match[1])
      .join('');
    const numericPattern = format.replace(/"[^"]*"/g, '');
    const decimalPart = numericPattern.includes('.')
      ? numericPattern.split('.')[1].replace(/[^0#]/g, '')
      : '';
    const minFractionDigits = decimalPart.split('').filter((char) => char === '0').length;
    const maxFractionDigits = decimalPart.length;
    const formatted = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: Math.max(minFractionDigits, maxFractionDigits)
    }).format(numericValue);

    return `${formatted}${suffix}`;
  }

  private toNumericValue(value: any): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!/^-?\d+([.,]\d+)?$/.test(trimmed)) {
      return null;
    }

    const numericValue = Number(trimmed.replace(',', '.'));
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private rawCellStyle(row: Record<string, any> | null, header: string): any {
    const cells = row?.['__style']?.cells || {};
    const dataKey = this.resolveRowKey(row, header);
    return cells[header] || cells[dataKey] || {};
  }

  private rawSlotCellStyle(row: Record<string, any> | null, key: string): any {
    const column = this.getSlotBinding(key);
    return this.mergeStyleObjects(
      column ? this.rawCellStyle(row, column) : {},
      this.rawSlotStyle(row, key)
    );
  }

  private rawSlotStyle(row: Record<string, any> | null, key: string): any {
    const slots = row?.['__style']?.slots || {};
    return slots[key] || {};
  }

  private rawDecorations(style: any, position: 'left' | 'right'): any[] {
    const key = position === 'right' ? 'rightDecorations' : 'leftDecorations';
    return Array.isArray(style?.[key]) ? style[key] : [];
  }

  private mergeStyleObjects(...styles: any[]): any {
    const result: any = {};
    for (const style of styles) {
      if (!style || typeof style !== 'object') {
        continue;
      }

      Object.keys(style).forEach((key) => {
        if (key !== 'leftDecorations' && key !== 'rightDecorations') {
          result[key] = style[key];
        }
      });
      if (Array.isArray(style.leftDecorations)) {
        result.leftDecorations = [...(result.leftDecorations || []), ...style.leftDecorations];
      }
      if (Array.isArray(style.rightDecorations)) {
        result.rightDecorations = [...(result.rightDecorations || []), ...style.rightDecorations];
      }
    }

    return result;
  }

  private resolveHeader(requestedColumn: string, headers: string[], rows: Record<string, any>[]): string {
    if (!requestedColumn) {
      return '';
    }

    return headers.find((header) => header === requestedColumn)
      || headers.find((header) => this.lookupKey(header) === this.lookupKey(requestedColumn))
      || Object.keys(rows[0] || {}).find((key) => key === requestedColumn)
      || Object.keys(rows[0] || {}).find((key) => this.lookupKey(key) === this.lookupKey(requestedColumn))
      || '';
  }

  private resolveRowKey(row: Record<string, any> | null, requestedColumn: string): string {
    if (!row || !requestedColumn) {
      return requestedColumn;
    }

    if (Object.prototype.hasOwnProperty.call(row, requestedColumn)) {
      return requestedColumn;
    }

    const lookup = this.lookupKey(requestedColumn);
    return Object.keys(row).find((key) => this.lookupKey(key) === lookup) || requestedColumn;
  }

  private uniqueColumns(columns: any[]): string[] {
    const result: string[] = [];
    for (const column of columns || []) {
      const value = String(column || '').trim();
      if (!value || result.some((item) => this.lookupKey(item) === this.lookupKey(value))) {
        continue;
      }

      result.push(value);
    }

    return result;
  }

  private lookupKey(value: any): string {
    return String(value || '')
      .trim()
      .replace(/\s+/g, '')
      .replace(/[_-]+/g, '')
      .toLowerCase();
  }

  private normalizeStyleObject(style: any): Record<string, string> {
    const result: Record<string, string> = {};
    if (style?.textColor) {
      result.color = style.textColor;
    }
    if (style?.backgroundColor) {
      result['background-color'] = style.backgroundColor;
    }
    return result;
  }

  private normalizeIconName(icon: unknown): string {
    if (icon === null || icon === undefined) {
      return '';
    }

    if (Array.isArray(icon)) {
      return this.normalizeIconName(icon.length >= 2 ? icon[1] : icon[0]);
    }

    if (typeof icon === 'object') {
      const iconLike = icon as { iconName?: unknown; name?: unknown; icon?: unknown };
      return this.normalizeIconName(iconLike.iconName ?? iconLike.name ?? iconLike.icon);
    }

    const raw = String(icon).trim();
    if (!raw) {
      return '';
    }

    const jsonCandidate = raw.replace(/'/g, '"');
    if (
      (jsonCandidate.startsWith('[') && jsonCandidate.endsWith(']')) ||
      (jsonCandidate.startsWith('{') && jsonCandidate.endsWith('}'))
    ) {
      try {
        return this.normalizeIconName(JSON.parse(jsonCandidate));
      } catch {
        return raw;
      }
    }

    if (!raw.startsWith('[') && !raw.startsWith('{') && raw.includes(':')) {
      return this.normalizeIconName(raw.split(':').pop());
    }

    return raw;
  }

  private matchesVisualVariant(variants: string[]): boolean {
    const candidates = [
      this.visualVariant,
      this.resolvedTemplateKey,
      this.renderer
    ].map((item) => this.lookupKey(item));

    return variants.some((variant) => candidates.includes(this.lookupKey(variant)));
  }
}
